import { useState, type ReactNode } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { theme } from './theme';
import { AuthProvider } from '@/auth/AuthContext';

/**
 * Wraps the app in the four cross-cutting providers, in order:
 *   1. QueryClient — caches API responses; stale-while-revalidate by default
 *   2. AuthProvider — holds JWT + scopes (placeholder, see AuthContext)
 *   3. ThemeProvider — MUI theme with mobile-first breakpoints
 *   4. CssBaseline — normalizes browser default styles
 *
 * Order matters: AuthProvider depends on QueryClient (so it can fire token-
 * refresh queries), and the rest are leaves.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Mobile-friendly: don't hammer the API on tab focus, and keep
            // results warm for 30s so back-button navigation feels instant.
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            retry: (failureCount, error) => {
              // Don't retry 4xx — they won't change without user action.
              const status = (error as { error?: { status?: number } } | null)?.error?.status;
              if (status && status >= 400 && status < 500) return false;
              return failureCount < 2;
            },
            refetchOnWindowFocus: false
          }
        }
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
